import React from "react";
import { useTranslation } from "@tecma/i18n";
import { Icon } from "@tecma/ui";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";

import { DefaultProps } from "utils/types/defaultProps";
import { PriceDetailItem } from "components/RecapSection/components/PriceDetailItem";
import { useDevice } from "hooks/device/device";
import { applicationStateSelectors } from "store/slices/applicationState/selectors";
import { applicationStateActions } from "store/slices/applicationState/slice";

import "./price-details.scss";

interface PriceDetailsRequiredProps {
  prices: {
    name: string;
    amount: number;
  }[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface PriceDetailsOptionalProps extends DefaultProps {}

export interface PriceDetailsProps
  extends PriceDetailsRequiredProps,
    PriceDetailsOptionalProps {}

const defaultProps: PriceDetailsOptionalProps = {
  "data-testid": "space-configurator-priceDetails",
};
const PriceDetails: React.FC<PriceDetailsProps> = ({ prices }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { type, typeDetailed } = useDevice();
  const isOpenRecap = useSelector(applicationStateSelectors.selectIsOpenRecap);
  const isOpenPriceDetails = useSelector(
    applicationStateSelectors.selectIsOpenPriceDetails,
  );
  const showCondoExpenses = useSelector(
    applicationStateSelectors.selectShowCondoExpenses,
  );
  const showTaxVatAmount = useSelector(
    applicationStateSelectors.selectShowTaxVatAmount,
  );

  const pricesToShow = prices.filter((price) => {
    if (showCondoExpenses && price.name === "building") {
      return true;
    }
    if (showTaxVatAmount && price.name === "taxesVAT") {
      return true;
    }
    return false;
  });

  const pricesDetailsClasses = classNames("price-details", {
    isOpenPriceDetails,
    isOpenRecap,
  });
  const pricesDetailsButtonClasses = classNames("price-details__button", {
    isOpenPriceDetails,
    isOpenRecap,
  });

  const pricesDetailsContentClasses = classNames("price-details__content", {
    isOpenPriceDetails:
      ["desktop_m", "desktop_l"].includes(typeDetailed) ||
      (type === "mobile"
        ? isOpenRecap && isOpenPriceDetails
        : isOpenPriceDetails),
  });

  const handleTogglePriceDetails = () => {
    if (isOpenPriceDetails) {
      dispatch(applicationStateActions.closePriceDetails());
    } else {
      dispatch(applicationStateActions.openPriceDetails());
    }
  };

  const getContent = () => {
    return (
      pricesToShow.length > 0 && (
        <>
          {!["desktop_m", "desktop_l"].includes(typeDetailed) && (
            <button
              type='button'
              className={pricesDetailsButtonClasses}
              onClick={() => handleTogglePriceDetails()}
            >
              <p className='price-details__button__text'>
                {t("recap.prices-details.see-detail", {
                  defaultValue: "See detail",
                })}
              </p>
              <Icon iconName='arrow-down' size='small' />
            </button>
          )}

          <div className={pricesDetailsContentClasses}>
            <div className='price-details__content__table'>
              <p className='price-details__content__title'>
                {t("recap.prices-details.title", {
                  defaultValue: "Price details",
                })}
              </p>
              {pricesToShow.map((price) => (
                <PriceDetailItem key={price.name} priceDetail={price} />
              ))}
            </div>
          </div>
        </>
      )
    );
  };

  return <div className={pricesDetailsClasses}>{getContent()}</div>;
};

PriceDetails.defaultProps = defaultProps as Partial<PriceDetailsOptionalProps>;

export default PriceDetails;
